import { AidaService, ELlmViews, LlmArtifactService } from '@adaptiva/ai/common';
import { Destroyer, filterNullish, isNullOrUndefined } from '@adaptiva/core/common';
import { Licensing } from '@adaptiva/core/licensing';
import { DynamicComponentLoaderService } from '@adaptiva/core/loaders';
import { IPanel, IPanelReference, PanelService } from '@adaptiva/core/panels';
import { UiService } from '@adaptiva/core/ui';
import { IOnboardingPanel, IOnboardingState, LicenseManagerService, ONBOARDING_PANELS_TOKEN, OnboardingService } from '@adaptiva/data-service';
import { ChangeDetectionStrategy, Component, computed, effect, EffectRef, EnvironmentInjector, inject, Injector, NgModule, signal, Signal, Type, WritableSignal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { forkJoin, map, Observable, tap } from 'rxjs';

@Component({
	standalone: false,
	selector: 'app-admin-portal-body',
	templateUrl: './admin-portal-body.component.html',
	styleUrl: './admin-portal-body.component.scss',
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AdminPortalBodyComponent extends Destroyer {

	protected readonly aida: AidaService = inject(AidaService);

	protected readonly licenseManager: LicenseManagerService = inject(LicenseManagerService);

	protected readonly componentLoader: DynamicComponentLoaderService = inject(DynamicComponentLoaderService);

	protected readonly environmentInjector: EnvironmentInjector = inject(EnvironmentInjector);

	protected readonly injector: Injector = inject(Injector);

	protected readonly llmArtifacts: LlmArtifactService = inject(LlmArtifactService);

	protected readonly llmDrawerWidth: WritableSignal<int> = signal(Math.min(480, Math.floor(window.innerWidth / 3)));

	protected readonly onboardingPanelMap: Map<string, IPanel> = new Map();

	protected readonly onboardingPanels: WritableSignal<IOnboardingPanel[]> = inject(ONBOARDING_PANELS_TOKEN);

	protected readonly onboardingService: OnboardingService = inject(OnboardingService);

	protected readonly panelService: PanelService = inject(PanelService);

	protected readonly ui: UiService = inject(UiService);

	protected onboardingPanelReference!: IPanelReference;

	protected readonly hasAidaLicense: Signal<boolean> = toSignal(
		this.licenseManager.licenseStates.pipe(
			filterNullish(),
			map((_states: Map<string, Licensing.States>) => {
				return _states.get(Licensing.Products.AddOns.AI) === Licensing.States.ENABLED;
			})
		)
	);

	protected readonly onboardingStateEffect: EffectRef = effect(() => {
		const _onboardingState: IOnboardingState | null = this.onboardingService.state();

		if (!isNullOrUndefined(_onboardingState)) {
			if (this.onboardingPanelReference)
				return;

			const _onboardingPanel: IPanel = this.getPanelFromState(_onboardingState);

			if (!_onboardingPanel)
				return;

			this.onboardingPanelReference = this.panelService.add(_onboardingPanel);
		} else {
			this.onboardingPanelReference?.remove();
			this.onboardingPanelReference = null;
		}
	});

	protected readonly onboardingPanelEffect: EffectRef = effect(() => {
		this.initializeOnboardingMap(this.onboardingPanels());
	});

	protected readonly splitterSizes: Signal<null | int[]> = computed(() => {
		return [null, ...this.panelService.panels().map(p => p.width)];
	});

	protected initializeOnboardingMap(_panels: IOnboardingPanel[]): void {
		if (!_panels?.length)
			return;

		const _observables: Observable<IPanel>[] = _panels.map((_onboardingPanel: IOnboardingPanel) => {
			return this.componentLoader.getComponentFactory<NgModule>(_onboardingPanel.componentId, this.environmentInjector).pipe(
				this.takeOneUntilDestroyed(),
				map((_componentType: Type<NgModule>) => ({
					componentType: _componentType,
					width: 330
				})),
				tap((_panel: IPanel) => {
					this.onboardingPanelMap.set(
						this.getOnboardingPanelName(_onboardingPanel.productName, _onboardingPanel.onboardingFlowName),
						_panel
					);
				})
			);
		});

		forkJoin(_observables).subscribe();
	}

	protected getOnboardingPanelName(_product: string, _flow: string): string {
		return `${_product}-${_flow}`;
	}

	protected getPanelFromState(_state: IOnboardingState): IPanel {
		const _onboardingName: string = this.getOnboardingPanelName(_state.productName, _state.onboardingFlowName);

		return this.onboardingPanelMap.get(_onboardingName) ?? null;
	}

	protected onLlmViewToggle(_event: MouseEvent): void {
		_event.stopImmediatePropagation();

		this.aida.view.set(this.aida.isChatView() ? ELlmViews.PRODUCT : ELlmViews.CHAT);
	}

}
